import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Group,
  Kbd,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Spotlight, spotlight, type SpotlightActionData } from "@mantine/spotlight";
import {
  IconChevronDown,
  IconChevronUp,
  IconHelp,
  IconLogout,
  IconMoon,
  IconSearch,
  IconSun,
} from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { Link, NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from "react-router";
import { semanticColor } from "@how/design-system";
import { CountBadge } from "./CountBadge";

/** The sidebar's width; the header's brand is centred over it. */
const NAVBAR_WIDTH = 256;

export interface AppFrameNavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  /** Match only the exact path (for "/"). */
  readonly end?: boolean;
  /** Something waiting there (the inbox's unread conversations): a badge beside the label, hidden at 0. */
  readonly count?: number;
  /** What the count counts, for the link's accessible name ("Communications, 3 unread"). */
  readonly countLabel?: string;
}

/**
 * Whether a navigation item matches the current path.
 *
 * Mantine's `NavLink` only sets `data-active` when it is given the `active` prop; wrapping it in a router NavLink is
 * not enough, because that only adds `aria-current`. Without this the active row fell back to Mantine's own tint and
 * none of the project's four navigation states applied. Exported so the matching rules are tested rather than
 * assumed.
 */
export function isNavItemActive(pathname: string, item: AppFrameNavItem): boolean {
  const exact = item.end ?? item.to === "/";
  if (exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * Whether a scroll viewport has more content above and below what is showing. The sidebar uses it to fade only the
 * edge that has something past it, so a short list never looks cut off and a long one says there is more.
 */
function useScrollEdges() {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (el === null) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);
  /**
   * Brings the selected row clear of the faded edges when the route changes. The floating sidebar (Horizon) is shorter
   * than the window, so on a laptop a row near the end, Settings, opened under the bottom fade with only its top showing.
   * Instant, not gliding: the list should already be in place when the page arrives.
   */
  const reveal = useCallback(() => {
    const el = ref.current;
    const row = el?.querySelector<HTMLElement>(".how-navlink[data-active]");
    if (el === null || row === null || row === undefined) return;
    const fade = SIDEBAR_FADE_PX;
    const top = row.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
    const bottom = top + row.offsetHeight;
    if (bottom > el.scrollTop + el.clientHeight - fade) {
      el.scrollTop = bottom - el.clientHeight + fade;
    } else if (top < el.scrollTop + fade) {
      el.scrollTop = Math.max(0, top - fade);
    }
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    update();
    if (typeof ResizeObserver === "undefined") return;
    // The viewport resizes with the window; its content resizes when navigation items come and go, which is also when
    // the selected row may first exist (items wait for permissions), so it is brought into view then too.
    const observer = new ResizeObserver(() => {
      reveal();
      update();
    });
    observer.observe(el);
    if (el.firstElementChild !== null) observer.observe(el.firstElementChild);
    return () => {
      observer.disconnect();
    };
  }, [update, reveal]);
  /** Most of a screenful toward one edge, gliding unless the person asked for reduced motion. */
  const page = useCallback((direction: 1 | -1) => {
    const el = ref.current;
    if (el === null) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ top: direction * el.clientHeight * 0.7, behavior: reduced ? "auto" : "smooth" });
  }, []);
  return { ref, edges, update, page, reveal };
}

/** The faded edge of the sidebar's list: `--how-sidebar-fade` in global.css. */
const SIDEBAR_FADE_PX = 44;

/** `to` is an in-app route; `href` a plain link (full page load); `onSelect` an action such as a hand-off. */
export type AppFrameAccountLink = {
  readonly label: string;
  /** Where it goes, shown quieter under the words (an address, R10); part of the item's accessible name. */
  readonly description?: string;
  readonly icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
} & ({ readonly to: string } | { readonly href: string } | { readonly onSelect: () => void });

/**
 * An account item's words and, under them, the quieter line naming where it goes. Spans, not divs: a menu item is a
 * button, which holds phrasing content only.
 */
export function AccountItemText({
  label,
  description,
}: {
  readonly label: string;
  readonly description?: string | undefined;
}) {
  if (description === undefined) return <>{label}</>;
  return (
    <>
      <Text span display="block" size="sm">
        {label}
      </Text>
      <Text span display="block" size="xs" c="dimmed">
        {description}
      </Text>
    </>
  );
}

/** The item's accessible name: its words, then where it goes when it names it (R10); otherwise the words alone. */
function accountLinkName(link: AppFrameAccountLink): string | undefined {
  return link.description === undefined ? undefined : `${link.label}, ${link.description}`;
}

export interface AppFrameAccount {
  readonly email: string;
  /** The person's name, for the initials on the account button; the email's are used without one. */
  readonly name?: string;
  /** Short descriptor under the email in the account menu (role, church). */
  readonly label?: string;
  /** Account pages (Security, Profile) listed above "Sign out". */
  readonly links?: readonly AppFrameAccountLink[];
  /**
   * HoW's help center (https://<platform>/help, ADR 0059): a "Help center" item in the account menu and in Spotlight,
   * opening in a new tab so the screen the person was on stays where it was.
   */
  readonly helpUrl?: string;
  readonly onSignOut: () => void;
}

export interface AppFrameProps {
  /** Left side of the header: product name, surface badge, tenant name. */
  readonly brand: ReactNode;
  /** Navigation is convenience, never authorization: filter items before passing them. */
  readonly nav: readonly AppFrameNavItem[];
  readonly account: AppFrameAccount;
  /** Extra Spotlight actions beyond "Go to <nav item>". */
  readonly actions?: readonly SpotlightActionData[];
  /** Rendered right after the brand: a tenant switcher, for example. */
  readonly switcher?: ReactNode;
  /** Rendered in the main area; defaults to the router Outlet. */
  readonly children?: ReactNode;
}

/**
 * The layer-2 shell in the Horizon design: a 56px header on the page ground, the sidebar as a card floating beside
 * the content (a drawer behind a burger on a phone), color-scheme toggle, account menu with sign-out, and Spotlight
 * (mod+K) wired to the navigation. Never frosted glass: blur is allowed only on modal and drawer scrims.
 */
export function AppFrame({ brand, nav, account, actions = [], switcher, children }: AppFrameProps) {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navScroll = useScrollEdges();
  const revealActive = navScroll.reveal;
  useEffect(() => {
    revealActive();
  }, [pathname, revealActive]);
  const spotlightActions: SpotlightActionData[] = [
    ...nav.map((item) => ({
      id: item.to,
      label: item.label,
      description: `Go to ${item.label}`,
      onClick: () => {
        void navigate(item.to);
      },
    })),
    ...actions,
    ...(account.helpUrl === undefined
      ? []
      : [
          {
            id: "how-help-center",
            label: "Help center",
            description: "Step-by-step answers, in a new tab",
            onClick: () => {
              window.open(account.helpUrl, "_blank", "noopener");
            },
          },
        ]),
  ];

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: NAVBAR_WIDTH, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding={{ base: "md", md: "xl" }}
    >
      <AppShell.Header className="how-appbar">
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation"
            />
            {/* Centred over the sidebar from the sm breakpoint up (owner, 2026-10-01, #325); see .how-appbar-brand. */}
            <Box
              className="how-appbar-brand"
              style={{ "--how-navbar-width": `${String(NAVBAR_WIDTH)}px` }}
            >
              {brand}
            </Box>
            {switcher}
          </Group>
          <Group gap="xs" wrap="nowrap">
            {/* A search field from the sm breakpoint up, so the shortcut is visible; the icon alone on a phone. */}
            <UnstyledButton
              className="how-search-pill"
              visibleFrom="sm"
              aria-label="Open search"
              onClick={spotlight.open}
            >
              <IconSearch size={15} stroke={1.8} aria-hidden />
              <span className="how-search-pill__label">Search…</span>
              <Kbd size="xs" aria-hidden>
                Ctrl K
              </Kbd>
            </UnstyledButton>
            <Tooltip label="Search (Ctrl+K)">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Open search"
                onClick={spotlight.open}
                hiddenFrom="sm"
              >
                <IconSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={colorScheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Toggle color scheme"
                onClick={toggleColorScheme}
              >
                {colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <Menu position="bottom-end" width={240}>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" radius="xl" aria-label="Account menu">
                  {/*
                   * Not `color="initials"`: Mantine picks a colour from the name out of a palette that knows nothing
                   * about our surfaces, and it landed on a green that measured 2.75:1 on a white header (2026-09-23).
                   * This is the signed-in person's own avatar, so the variety bought nothing anyway. The brand fill
                   * is the one pair the theme tests hold to AA in both schemes.
                   */}
                  <Avatar
                    size="sm"
                    radius="xl"
                    name={account.name?.trim() || account.email}
                    color="brand"
                    variant="filled"
                  />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="xs" truncate>
                    {account.email}
                  </Text>
                </Menu.Label>
                {/*
                 * A plain line, not a disabled item: Mantine fades a disabled item to 0.6, which left this badge at
                 * 2.52:1 light and 2.83:1 dark (ruling D14). It names the account; there is nothing to choose.
                 */}
                {account.label === undefined ? null : (
                  <Box px="sm" pb={6}>
                    <Badge variant="light" size="sm" color={semanticColor("neutral")}>
                      {account.label}
                    </Badge>
                  </Box>
                )}
                <Menu.Divider />
                {(account.links ?? []).map((link) => {
                  const shared = {
                    leftSection: <link.icon size={16} />,
                    "aria-label": accountLinkName(link),
                    children: <AccountItemText label={link.label} description={link.description} />,
                  };
                  return "onSelect" in link ? (
                    <Menu.Item key={link.label} onClick={link.onSelect} {...shared} />
                  ) : "href" in link ? (
                    <Menu.Item key={link.href} component="a" href={link.href} {...shared} />
                  ) : (
                    <Menu.Item key={link.to} component={Link} to={link.to} {...shared} />
                  );
                })}
                {account.helpUrl === undefined ? null : (
                  <Menu.Item
                    component="a"
                    href={account.helpUrl}
                    target="_blank"
                    rel="noopener"
                    aria-label="Help center (opens in a new tab)"
                    leftSection={<IconHelp size={16} />}
                  >
                    Help center
                  </Menu.Item>
                )}
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={account.onSignOut}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="sm" className="how-sidebar">
        {/*
         * Where the list runs past an edge, that edge fades and a small round button floats over it; the scrollbar
         * itself is a slim neutral pill that appears under the pointer, in the gutter beside the rows
         * (.how-sidebar-nav).
         */}
        <AppShell.Section
          grow
          className="how-sidebar-nav"
          data-fade-top={navScroll.edges.top || undefined}
          data-fade-bottom={navScroll.edges.bottom || undefined}
        >
          {/*
           * Pointer conveniences only: keyboard focus already scrolls each link into view, so the buttons stay out of
           * the tab order and out of the accessibility tree.
           */}
          <UnstyledButton
            className="how-sidebar-more"
            data-edge="top"
            tabIndex={-1}
            aria-hidden
            onClick={() => {
              navScroll.page(-1);
            }}
          >
            <IconChevronUp size={14} stroke={2.2} />
          </UnstyledButton>
          {/* No scrollbar: the list scrolls by wheel, touch and keyboard, and the arrows above and below page it. */}
          <ScrollArea
            className="how-sidebar-scroll"
            type="never"
            viewportRef={navScroll.ref}
            onScrollPositionChange={navScroll.update}
          >
            {nav.map((item) => (
              <NavLink
                key={item.to}
                component={RouterNavLink}
                to={item.to}
                end={item.end ?? item.to === "/"}
                active={isNavItemActive(
                  // The design previews (/home2/1 to /home2/4) are the home page in another design.
                  /^\/home2(\/|$)/.test(location.pathname) ? "/" : location.pathname,
                  item,
                )}
                label={item.label}
                leftSection={<item.icon size={20} stroke={1.7} />}
                {...((item.count ?? 0) > 0
                  ? {
                      // Visible words first (SC 2.5.3): "Communications, 3 unread".
                      "aria-label": `${item.label}, ${String(item.count)}${item.countLabel === undefined ? "" : ` ${item.countLabel}`}`,
                      rightSection: <CountBadge count={item.count ?? 0} />,
                    }
                  : {})}
                onClick={close}
              />
            ))}
          </ScrollArea>
          <UnstyledButton
            className="how-sidebar-more"
            data-edge="bottom"
            tabIndex={-1}
            aria-hidden
            onClick={() => {
              navScroll.page(1);
            }}
          >
            <IconChevronDown size={14} stroke={2.2} />
          </UnstyledButton>
        </AppShell.Section>
        {/* The signed-in person, pinned to the foot of the sidebar, with sign-out one click away: their name and
            initials when the profile has them (as the account button above), the email's first half otherwise. */}
        <AppShell.Section className="how-sidebar-account">
          <Group gap="sm" wrap="nowrap">
            <Avatar
              size={34}
              radius="xl"
              name={account.name?.trim() || account.email}
              color="brand"
              variant="filled"
            />
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Text size="sm" fw={600} truncate>
                {account.name?.trim() || account.email.split("@")[0]}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {account.label ?? account.email}
              </Text>
            </Box>
            <ActionIcon
              variant="subtle"
              color="gray"
              radius="xl"
              aria-label="Sign out"
              onClick={account.onSignOut}
            >
              <IconLogout size={18} />
            </ActionIcon>
          </Group>
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main className="how-main">
        {/* Keyed to the route so each page arrives with the same short rise (.how-page-enter). */}
        <Box key={pathname} className="how-page-enter how-content">
          {children ?? <Outlet />}
        </Box>
      </AppShell.Main>
      <Spotlight
        actions={spotlightActions}
        shortcut={["mod + K"]}
        nothingFound="Nothing found"
        searchProps={{ placeholder: "Search" }}
      />
    </AppShell>
  );
}
